import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "theme";
type Theme = "light" | "dark" | "system";

export function useColorScheme(): [Theme, (theme: Theme) => void] {
    const [themeState, setThemeState] = useState<Theme>("system");

    const setTheme = useCallback((theme: Theme) => {
        const root = document.documentElement;
        const isDark = theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);

        root.setAttribute("data-color-scheme", isDark ? "dark" : "light");
        localStorage.setItem(STORAGE_KEY, theme);
        setThemeState(theme);
    }, []);

    useEffect(() => {
        const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;

        if (stored === "light" || stored === "dark" || stored === "system") {
            setTheme(stored);
        } else {
            setTheme("system");
        }
    }, [setTheme]);

    useEffect(() => {
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const handler = () => {
            if (themeState === "system") setTheme("system");
        };

        if (themeState === "system") {
            mq.addEventListener("change", handler);
            return () => mq.removeEventListener("change", handler);
        }
    }, [themeState, setTheme]);

    return [themeState, setTheme] as const;
}
